import React from 'react';
import { InView } from 'react-intersection-observer';

const AboutUs = () => {
  const features = [
    {
      icon: "✓",
      text: "Wide range of premium vehicles"
    },
    {
      icon: "⏱",
      text: "Flexible rental periods"
    },
    {
      icon: "👥",
      text: "24/7 customer support"
    },
    {
      icon: "🛡",
      text: "Secure transactions & insurance"
    }
  ];

  return (
    <div className="py-20 bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-72 h-72 bg-cyan-500 rounded-full mix-blend-screen filter blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-0 w-72 h-72 bg-purple-500 rounded-full mix-blend-screen filter blur-3xl animate-pulse animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-blue-500 rounded-full mix-blend-screen filter blur-3xl animate-pulse animation-delay-4000"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-12">
          {/* Left Section - Content */}
          <InView triggerOnce={true} threshold={0.3}>
            {({ inView, ref }) => (
              <div
                ref={ref}
                className={`lg:w-1/2 text-white ${inView ? 'animate__animated animate__fadeInLeft' : ''}`}
              >
                {/* Section Header */}
                <div className="mb-8">
                  <h2 className="text-4xl lg:text-5xl font-bold mb-6">
                    About <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Nupura Self Drive Cars</span>
                  </h2>
                  <div className="w-20 h-1 bg-gradient-to-r from-cyan-400 to-blue-400 rounded-full mb-6"></div>
                </div>

                {/* Main Content */}
                <div className="space-y-6">
                  <p className="text-xl leading-relaxed text-blue-100">
                    At <span className="font-semibold text-cyan-300">Nupura Self Drive Cars</span>, we revolutionize car rental experiences by offering seamless, hassle-free vehicle rentals tailored to your unique travel needs.
                  </p>

                  <p className="text-lg leading-relaxed text-blue-100">
                    Whether you're embarking on an epic road trip, managing business travel, or simply need reliable transportation, our diverse fleet of meticulously maintained vehicles is ready to serve you.
                  </p>

                  <p className="text-lg leading-relaxed text-blue-100">
                    From economy cars to luxury SUVs, we cater to every preference and budget, ensuring your journey is comfortable, safe, and memorable.
                  </p>
                </div>

                {/* Features Grid */}
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {features.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-3 bg-white/5 backdrop-blur-sm rounded-lg p-4 border border-white/10 hover:border-cyan-400/30 transition-all duration-300">
                      <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                        {feature.icon}
                      </div>
                      <span className="text-blue-100 font-medium">{feature.text}</span>
                    </div>
                  ))}
                </div>

                {/* CTA Button */}
                <div className="mt-8">
                  <button className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-bold py-3 px-8 rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300">
                    Discover Our Fleet
                  </button>
                </div>
              </div>
            )}
          </InView>

          {/* Right Section - Enhanced Image */}
          <InView triggerOnce={true} threshold={0.3}>
            {({ inView, ref }) => (
              <div
                ref={ref}
                className={`lg:w-1/2 relative ${inView ? 'animate__animated animate__fadeInRight' : ''}`}
              >
                {/* Main Image Container */}
                <div className="relative group">
                  {/* Background Glow */}
                  <div className="absolute -inset-4 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl blur-lg opacity-25 group-hover:opacity-40 transition duration-1000 group-hover:duration-200 animate-tilt"></div>
                  
                  {/* Image with Border */}
                  <div className="relative rounded-2xl overflow-hidden border-2 border-white/20 backdrop-blur-sm">
                    <img
                      src="https://static.vecteezy.com/system/resources/thumbnails/012/939/271/original/animated-presentation-isolated-scene-strategy-meeting-business-plan-looped-flat-2d-characters-4k-footage-with-alpha-channel-colorful-illustration-for-mobile-website-animation-video.jpg"
                      alt="Nupura Self Drive Cars Team"
                      className="w-full h-[400px] lg:h-[500px] object-cover transform group-hover:scale-110 transition-transform duration-700"
                    />
                    
                    {/* Overlay Gradient */}
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent"></div>
                    
                    {/* Floating Elements */}
                    <div className="absolute bottom-6 left-6 right-6">
                      <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20">
                        <h3 className="text-white font-bold text-lg mb-2">Premium Service Guaranteed</h3>
                        <p className="text-blue-100 text-sm">Experience the difference with our customer-first approach</p>
                      </div>
                    </div>
                  </div>

                  {/* Floating Stats */}
                  <div className="absolute -top-4 -right-4 bg-gradient-to-r from-cyan-500 to-blue-500 text-white py-2 px-4 rounded-full shadow-2xl transform rotate-6">
                    <div className="text-sm font-bold">Since 2025</div>
                  </div>

                  <div className="absolute -bottom-4 -left-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white py-2 px-4 rounded-full shadow-2xl transform -rotate-6">
                    <div className="text-sm font-bold">10K+ Customers</div>
                  </div>
                </div>

                {/* Additional Info Cards */}
                <div className="grid grid-cols-2 gap-4 mt-8">
                  <div className="bg-white/5 backdrop-blur-md rounded-xl p-4 border border-white/10 text-center hover:bg-white/10 transition-all duration-300">
                    <div className="text-2xl font-bold text-cyan-400">500+</div>
                    <div className="text-blue-200 text-sm">Vehicles</div>
                  </div>
                  <div className="bg-white/5 backdrop-blur-md rounded-xl p-4 border border-white/10 text-center hover:bg-white/10 transition-all duration-300">
                    <div className="text-2xl font-bold text-green-400">50+</div>
                    <div className="text-blue-200 text-sm">Cities</div>
                  </div>
                </div>
              </div>
            )}
          </InView>
        </div>
      </div>

      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes tilt {
          0%, 50%, 100% {
            transform: rotate(0deg);
          }
          25% {
            transform: rotate(0.5deg);
          }
          75% {
            transform: rotate(-0.5deg);
          }
        }
        .animate-tilt {
          animation: tilt 10s infinite linear;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default AboutUs;